import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from '../../../node_modules/prop-types/index.js';
import Errors from '../Errors/Errors.js';
import Legend from '../Legend/Legend.js';
import { hasErrors, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { Button, Root, Fieldset, TooltipContainer, StackedWrapper, ButtonSelectorWrapper } from './styledComponents.js';
import useTheme from '../../hooks/useTheme.js';
import useMedia from '../../hooks/useMedia.js';
import componentWithName from '../../prop-types/componentWithName.js';

var ButtonSelector = function ButtonSelector(_ref) {
  var id = _ref.id,
    label = _ref.label,
    value = _ref.value,
    values = _ref.values,
    tooltip = _ref.tooltip,
    ariaLabel = _ref.ariaLabel,
    errors = _ref.errors,
    hideOutline = _ref.hideOutline,
    disabled = _ref.disabled,
    required = _ref.required,
    callouts = _ref.callouts,
    verticalStackingBreakPoint = _ref.verticalStackingBreakPoint,
    onChange = _ref.onChange,
    customStyle = _ref.customStyle;
  var theme = useTheme();
  var _safeCustomStyle = safeCustomStyle(customStyle),
    rootStyle = _safeCustomStyle.rootStyle;
  var hasError = hasErrors(errors);
  var errorsId = hasError ? "".concat(id, "-errors") : undefined;
  var showErrorOutline = hideOutline === true ? false : hasError;
  var _useState = useState(value),
    _useState2 = _slicedToArray(_useState, 2),
    val = _useState2[0],
    setValue = _useState2[1];
  useEffect(function () {
    if (value !== val) {
      setValue(value);
    }
    // NOTE: Ignoring the `val` prop here; we don't actually want this hook to run if `val` changes, only `value`
    // This avoids a duplicate rerender when a user updates the value by clicking the button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  var buttonChange = useCallback(function (e) {
    setValue(e.target.value);
    handle(onChange)(e);
  }, [onChange]);
  var isVerticallyStacked = useMedia(["(max-width: ".concat(verticalStackingBreakPoint, "px)")],
  // media queries
  [true],
  // values corresponding to media query
  false // default value
  );

  var buttonGroup = values.map(function (obj, index) {
    return /*#__PURE__*/React.createElement(Button, {
      id: "".concat(id, "-").concat(index),
      type: "button",
      key: obj.value,
      isLastButton: index === values.length - 1,
      isVerticallyStacked: isVerticallyStacked,
      showErrorOutline: showErrorOutline,
      value: obj.value,
      onClick: buttonChange,
      disabled: disabled,
      selected: obj.value === val,
      "aria-describedby": errorsId,
      "aria-checked": obj.value === val,
      role: "radio",
      theme: theme
    }, obj.label);
  });
  return /*#__PURE__*/React.createElement(Root, {
    rootStyle: rootStyle
  }, /*#__PURE__*/React.createElement(Fieldset, {
    "aria-required": required,
    role: "radiogroup",
    "aria-label": ariaLabel
  }, label && /*#__PURE__*/React.createElement(Legend, {
    id: "".concat(id, "-label")
  }, label, tooltip && /*#__PURE__*/React.createElement(TooltipContainer, {
    "data-testid": "tooltip-test"
  }, tooltip)), isVerticallyStacked ? /*#__PURE__*/React.createElement(StackedWrapper, {
    verticalStackingBreakPoint: verticalStackingBreakPoint,
    role: "presentation",
    "data-testid": "vertical-wrapper"
  }, buttonGroup) : /*#__PURE__*/React.createElement(ButtonSelectorWrapper, {
    "data-testid": "horizontal-wrapper"
  }, buttonGroup), /*#__PURE__*/React.createElement(Errors, {
    id: id,
    errors: errors,
    callouts: callouts
  })));
};
process.env.NODE_ENV !== "production" ? ButtonSelector.propTypes = {
  /** DOM element id */
  id: PropTypes.string.isRequired,
  /** label component for input */
  label: PropTypes.node,
  /** currently selected value */
  value: PropTypes.string,
  /** button options */
  values: PropTypes.arrayOf(PropTypes.shape({
    /** value for the item, equivalent to option value in select element */
    value: PropTypes.string.isRequired,
    /** label for the item, equivalent to option label in select element */
    label: PropTypes.string.isRequired
  })),
  /** [Tooltip](/docs/tooltip) displayed beside the label */
  tooltip: componentWithName('Tooltip'),
  /** aria-label for the button selector */
  ariaLabel: PropTypes.string,
  /** hide error outline */
  hideOutline: PropTypes.bool,
  /** HTML 5 disabled attribute */
  disabled: PropTypes.bool,
  /** Required / Optional */
  required: PropTypes.bool,
  /** sets aria-live to 'polite' and role to 'alert' on error message block  */
  callouts: PropTypes.bool,
  /** breakpoint after which the buttons display horizontally */
  verticalStackingBreakPoint: PropTypes.number,
  /** handler for click on button selector */
  onChange: PropTypes.func,
  /** Style overrides */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object
  }),
  /** error array */
  errors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    // error id
    msg: PropTypes.string,
    // localized error text
    isError: PropTypes.bool // error condition
  }))
} : void 0;
ButtonSelector.defaultProps = {
  label: '',
  value: '',
  values: [],
  tooltip: undefined,
  ariaLabel: undefined,
  hideOutline: false,
  disabled: false,
  required: true,
  callouts: true,
  verticalStackingBreakPoint: 575,
  onChange: noop,
  customStyle: {
    rootStyle: {}
  },
  errors: []
};
process.env.NODE_ENV !== "production" ? ButtonSelector.displayName = 'ButtonSelector' : void 0;
var ButtonSelector$1 = ButtonSelector;

export { ButtonSelector$1 as default };
